import type { ColumnMapping, SourceType } from '../types'
import {
  HEADER_SCAN_NON_EMPTY_ROWS,
  type FormulaResultState,
  type ImportedCell,
  type ImportedRow,
  type ImportedSheetMetadata,
  type ParsedSheet,
  type SheetScanRow,
} from '../io/import/types'
import { mapSheetToBoqRows, type MappedBoqSheet } from '../io/mapSheetToBoqRows'

export type DemoPrimitive = string | number | boolean | null

export interface DemoFormulaValue {
  formula: string
  result: DemoPrimitive
}

export interface DemoCellSpec {
  value: DemoPrimitive | DemoFormulaValue
  numberFormat?: string
}

export type DemoCellValue = DemoPrimitive | DemoCellSpec

export interface DemoWorkbookDefinition {
  sourceType: SourceType
  fileName: string
  sheetName: string
  headerRow: number
  mapping: ColumnMapping
  rows: readonly (readonly DemoCellValue[])[]
}

export const DEMO_FIXED_DATE = new Date('2024-01-15T08:00:00.000Z')

export const DEMO_BASELINE_MAPPING: ColumnMapping = {
  headerRow: 4,
  itemCodeColumn: 1,
  itemNameColumn: 2,
  itemDescriptionColumn: 3,
  unitColumn: 4,
  quantityColumn: 5,
  unitPriceColumn: 6,
  totalPriceColumn: 7,
}

export const DEMO_TARGET_MAPPING: ColumnMapping = {
  headerRow: 5,
  itemCodeColumn: 1,
  itemNameColumn: 0,
  itemDescriptionColumn: 3,
  unitColumn: 2,
  quantityColumn: 4,
  unitPriceColumn: 5,
  totalPriceColumn: 6,
}

function formula(formulaText: string, result: number): DemoCellSpec {
  return { value: { formula: formulaText, result }, numberFormat: '#,##0.00' }
}

function formatted(value: DemoPrimitive, numberFormat: string): DemoCellSpec {
  return { value, numberFormat }
}

/**
 * All names, materials, quantities and prices below are invented solely for
 * the BOQ Diff demo. They do not represent any real project or vendor export.
 */
export const DEMO_BASELINE_WORKBOOK: DemoWorkbookDefinition = {
  sourceType: 'baseline',
  fileName: 'demo-baseline.xlsx',
  sheetName: '虚构清单_基准',
  headerRow: 4,
  mapping: DEMO_BASELINE_MAPPING,
  rows: [
    ['清单差异雷达演示数据（完全虚构）'],
    ['仅用于软件测试；不代表任何真实项目、企业或计价数据库。'],
    [null],
    [
      '序号',
      '项目编码',
      '项目名称',
      '项目特征描述',
      '计量单位',
      '工程量',
      '综合单价',
      '合价',
      '备注',
    ],
    [null, null, '分部工程 星雾构件（虚构章节）'],
    [1, '001001', '星砂找平层', '虚构材料A，厚度20mm', 'm²', 120.5, 48, 5_784, '完全不变'],
    [2, '001002', '云纹隔墙板', '轻质芯材，厚度75mm', 'm2', 200, 85.5, 17_100, '仅工程量变化'],
    [3, '001003', '雾蓝踢脚线', '高分子条材，高度100mm', 'm', 350, 22.3, 7_805, '仅综合单价变化'],
    [4, '001004', '月桂防护层', '水性涂层，两遍成活', '㎡', 88, 62, 5_456, '项目特征变化'],
    [5, '001005', '晨曦检修口', '铝合金，450×450mm', '个', 12, 185, 2_220, '编码变化'],
    [6, '001006', '纸鹤标识牌', '复合板，300×200mm', '块', 7, 260, 1_820, '对比版删除'],
    [7, '002001', '青禾立柱', '方形构件，100×100mm', '根', 24, 310, 7_440, '重复编码一'],
    [8, '002001', '青禾横梁', '矩形构件，80×60mm', '根', 36, 220, 7_920, '重复编码二'],
    [9, null, '浮光密封条', '弹性条材，宽度25mm', 'm', 1_000, 12.5, 12_500, '缺失编码'],
    [
      10,
      formatted(2_002, '000000'),
      '澄空收口片',
      '虚构合金，厚度1.2mm',
      '片',
      64,
      56.25,
      3_600,
      '前导零编码',
    ],
    [
      11,
      '003001',
      '星点照明盒',
      '嵌入式，预留深度90mm',
      '个',
      40,
      73.5,
      null,
      '合价由数量×单价计算',
    ],
    [12, null, '环形收边构件', '银灰金属，宽度42mm，亚光', 'm', 96, 31, 2_976, '模糊待确认'],
    [
      13,
      '004001',
      '远岚吸声板',
      '虚构材料B，600×600mm',
      '㎡',
      formatted(1_234.5, '#,##0.000'),
      19.8,
      24_443.1,
      '千分位数字',
    ],
    [
      null,
      null,
      '第一分部合计',
      null,
      null,
      null,
      null,
      formula('SUM(H6:H18)', 102_004.1),
      '合计行不参与',
    ],
    [null, null, '说明：以上名称和数值均为随机编制。'],
  ],
}

export const DEMO_TARGET_WORKBOOK: DemoWorkbookDefinition = {
  sourceType: 'target',
  fileName: 'demo-target.xlsx',
  sheetName: '虚构清单_对比',
  headerRow: 5,
  mapping: DEMO_TARGET_MAPPING,
  rows: [
    ['清单差异雷达演示数据（完全虚构）'],
    ['字段顺序和标题故意与基准版不同，用于验证独立映射。'],
    ['版本', '对比版演示'],
    [null],
    [
      '清单项目名称',
      '清单项目编码',
      '单位',
      '项目特征',
      '数量',
      '含税综合单价',
      '金额',
      '复核备注',
      '序号',
    ],
    ['分部工程 星雾构件（虚构章节）'],
    ['星砂找平层', '001001', '㎡', '虚构材料A，厚度20mm', 120.5, 48, 5_784, '完全不变', 1],
    [
      '云纹隔墙板',
      '001002',
      '㎡',
      '轻质芯材，厚度75mm',
      225,
      85.5,
      19_237.5,
      '工程量由200调整为225',
      2,
    ],
    [
      '雾蓝踢脚线',
      '001003',
      'm',
      '高分子条材，高度100mm',
      350,
      24.1,
      8_435,
      '单价由22.30调整为24.10',
      3,
    ],
    ['月桂防护层', '001004', 'm²', '水性涂层，三遍成活', 88, 62, 5_456, '项目特征改变', 4],
    ['晨曦检修口', '009905', '个', '铝合金，450×450mm', 12, 185, 2_220, '编码改变，其余一致', 5],
    ['青禾横梁', '002001', '根', '矩形构件，80×60mm', 36, 220, 7_920, '重复编码且顺序交换', 6],
    ['青禾立柱', '002001', '根', '方形构件，100×100mm', 24, 310, 7_440, '重复编码且顺序交换', 7],
    ['浮光密封条', null, 'm', '弹性条材，宽度25mm', 1_000, 12.5, 12_500, '两版均缺失编码', 8],
    [
      '澄空收口片',
      '002002',
      '片',
      '虚构合金，厚度1.2mm',
      64,
      56.25,
      3_600,
      '字符串形式前导零编码',
      9,
    ],
    [
      '星点照明盒',
      '003001',
      '个',
      '嵌入式，预留深度90mm',
      40,
      73.5,
      null,
      '合价由数量×单价计算',
      10,
    ],
    [
      '环形收边条 A',
      null,
      'm',
      '银灰金属，宽度40mm，亚光',
      50,
      31,
      1_550,
      '与基准项相近候选一',
      11,
    ],
    [
      '环形收边条 B',
      null,
      'm',
      '银灰金属，宽度44mm，亚光',
      46,
      31,
      1_426,
      '与基准项相近候选二',
      12,
    ],
    [
      '远岚吸声板',
      '004001',
      'm2',
      '虚构材料B，600×600mm',
      formatted(1_234.5, '#,##0.000'),
      19.8,
      24_443.1,
      '千分位数字',
      13,
    ],
    ['霞光边界盖板', '005001', '块', '虚构复材，500×300mm', 18, 142, 2_556, '新增项目', 14],
    [
      '第一分部合计',
      null,
      null,
      null,
      null,
      null,
      formula('SUM(G7:G20)', 105_507.6),
      '合计行不参与',
    ],
    ['说明：本演示数据不对应任何真实工程。'],
  ],
}

export const DEMO_WORKBOOKS = [DEMO_BASELINE_WORKBOOK, DEMO_TARGET_WORKBOOK] as const

export const DEMO_COVERED_CASES = [
  '完全不变项目',
  '仅工程量变化',
  '仅综合单价变化',
  '项目特征变化',
  '项目编码变化但其他特征高度一致',
  '新增项目',
  '删除项目',
  '重复编码',
  '缺失编码',
  '千分位数字',
  '前导零编码',
  '合价为空但可由工程量乘综合单价计算',
  '无法自动确认的模糊匹配',
  '合计行',
  '章节标题行',
  '不同的字段列顺序和列名',
] as const

export function isDemoCellSpec(value: DemoCellValue): value is DemoCellSpec {
  return typeof value === 'object' && value !== null && 'value' in value
}

export function isDemoFormulaValue(value: unknown): value is DemoFormulaValue {
  return typeof value === 'object' && value !== null && 'formula' in value && 'result' in value
}

function leadingZeroDisplay(value: number, numberFormat?: string): string | null {
  if (!numberFormat || !Number.isInteger(value) || !/^0+$/.test(numberFormat)) return null
  return String(value).padStart(numberFormat.length, '0')
}

export function demoCellToImportedCell(value: DemoCellValue): ImportedCell {
  const spec = isDemoCellSpec(value) ? value : { value }
  const numberFormat = spec.numberFormat
  if (isDemoFormulaValue(spec.value)) {
    const state: FormulaResultState = spec.value.result === null ? 'missing' : 'cached'
    return {
      rawValue: spec.value.result,
      displayText: spec.value.result === null ? '' : String(spec.value.result),
      kind: 'formula',
      formula: spec.value.formula,
      formulaResultState: state,
      ...(numberFormat ? { numberFormat } : {}),
    }
  }
  if (spec.value === null) return { rawValue: null, displayText: '', kind: 'blank' }
  if (typeof spec.value === 'number') {
    return {
      rawValue: spec.value,
      displayText: leadingZeroDisplay(spec.value, numberFormat) ?? String(spec.value),
      kind: 'number',
      ...(numberFormat ? { numberFormat } : {}),
    }
  }
  return {
    rawValue: spec.value,
    displayText: String(spec.value),
    kind: typeof spec.value === 'boolean' ? 'boolean' : 'text',
    ...(numberFormat ? { numberFormat } : {}),
  }
}

function importedRows(definition: DemoWorkbookDefinition): ImportedRow[] {
  return definition.rows.map((values, index) => ({
    sourceRowNumber: index + 1,
    cells: values.map(demoCellToImportedCell),
  }))
}

function rowNonEmptyCount(row: ImportedRow): number {
  return row.cells.filter((cell) => cell.rawValue !== null || cell.displayText.trim() !== '').length
}

function buildDemoMetadata(
  definition: DemoWorkbookDefinition,
  rows: ImportedRow[],
): ImportedSheetMetadata {
  const nonEmpty = rows.filter((row) => rowNonEmptyCount(row) > 0)
  const scanRows: SheetScanRow[] = nonEmpty
    .slice(0, HEADER_SCAN_NON_EMPTY_ROWS)
    .map((row) => ({ ...row, nonEmptyCellCount: rowNonEmptyCount(row) }))
  return {
    name: definition.sheetName,
    index: 0,
    visibility: 'visible',
    declaredRowCount: definition.rows.length,
    declaredColumnCount: Math.max(...definition.rows.map((row) => row.length)),
    nonEmptyRowCount: nonEmpty.length,
    firstNonEmptyRowNumber: nonEmpty[0]?.sourceRowNumber ?? null,
    lastNonEmptyRowNumber: nonEmpty.at(-1)?.sourceRowNumber ?? null,
    potentiallyExceedsDataRowLimit: false,
    scanRows,
  }
}

export function createDemoParsedSheet(sourceType: SourceType): ParsedSheet {
  const definition = sourceType === 'baseline' ? DEMO_BASELINE_WORKBOOK : DEMO_TARGET_WORKBOOK
  const allRows = importedRows(definition)
  const sheet = buildDemoMetadata(definition, allRows)
  const headerCells = allRows[definition.headerRow - 1]?.cells ?? []
  const rows = allRows.filter(
    (row) => row.sourceRowNumber > definition.headerRow && rowNonEmptyCount(row) > 0,
  )
  return {
    workbookId: `demo-${sourceType}`,
    sourceType,
    fileName: definition.fileName,
    fileSize: 0,
    format: 'xlsx',
    sheet,
    headerRow: definition.headerRow,
    headerCells,
    rows,
    previewRows: rows.slice(0, 10),
    effectiveDataRowCount: rows.length,
    warnings: [],
  }
}

export function createDemoMappedSheet(sourceType: SourceType): MappedBoqSheet {
  const definition = sourceType === 'baseline' ? DEMO_BASELINE_WORKBOOK : DEMO_TARGET_WORKBOOK
  return mapSheetToBoqRows(createDemoParsedSheet(sourceType), definition.mapping)
}

export function createDemoComparisonRows(): {
  baseline: MappedBoqSheet
  target: MappedBoqSheet
} {
  return {
    baseline: createDemoMappedSheet('baseline'),
    target: createDemoMappedSheet('target'),
  }
}
